import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

const emptyForm = { course_id: '', name: '', description: '', topic: '', difficulty_level: 'mixed' };

const actionIconStyle = { width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 };
const solidGreen = { backgroundColor: '#198754', borderColor: '#198754', color: '#fff', opacity: 1 };
const solidRed = { backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff', opacity: 1 };

function AdminMockExamBanks() {
  const navigate = useNavigate();
  const [banks, setBanks] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [courseFilter, setCourseFilter] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [search, setSearch] = useState('');

  const [quickTestBank, setQuickTestBank] = useState(null);
  const [quickTestCount, setQuickTestCount] = useState(10);

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchCourses = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/courses`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) setCourses(response.data.courses || []);
    } catch (err) {
      // non-fatal
    }
  }, []);

  const fetchBanks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) {
        setError('Authentication required.');
        setLoading(false);
        return;
      }
      const params = {};
      if (courseFilter) params.courseId = courseFilter;
      if (search) params.search = search;
      const response = await axios.get(`${apiUrl}/api/admin/mock-exams/banks`, { headers: { Authorization: `Bearer ${token}` }, params });
      if (response.data.success) {
        setBanks(response.data.banks || []);
      } else {
        setError(response.data.message || 'Failed to fetch question banks.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching question banks.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseFilter, search]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);
  useEffect(() => {
    const timer = setTimeout(() => fetchBanks(), 250);
    return () => clearTimeout(timer);
  }, [fetchBanks]);

  const filteredBanks = banks.filter((b) => {
    if (topicFilter && (b.topic || '').toLowerCase().indexOf(topicFilter.toLowerCase()) === -1) return false;
    if (difficultyFilter && b.difficulty_level !== difficultyFilter) return false;
    return true;
  });

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const startEdit = (bank) => {
    setForm({
      course_id: bank.course_id,
      name: bank.name,
      description: bank.description || '',
      topic: bank.topic || '',
      difficulty_level: bank.difficulty_level,
    });
    setEditingId(bank.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!form.course_id || !form.name.trim()) {
      setError('Course and name are required.');
      return;
    }
    setSaving(true);
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      let response;
      if (editingId) {
        response = await axios.put(`${apiUrl}/api/admin/mock-exams/banks/${editingId}`, form, { headers });
      } else {
        response = await axios.post(`${apiUrl}/api/admin/mock-exams/banks`, form, { headers });
      }
      if (response.data.success) {
        setSuccess(editingId ? 'Question bank updated.' : 'Question bank created.');
        resetForm();
        fetchBanks();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to save question bank.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving question bank.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (bank) => {
    try {
      const token = getToken();
      const newStatus = bank.status === 'active' ? 'inactive' : 'active';
      await axios.put(`${apiUrl}/api/admin/mock-exams/banks/${bank.id}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      fetchBanks();
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating status.');
    }
  };

  const handleDelete = async (bank) => {
    if (!window.confirm(`Delete question bank "${bank.name}"? This cannot be undone.`)) return;
    try {
      const token = getToken();
      const response = await axios.delete(`${apiUrl}/api/admin/mock-exams/banks/${bank.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        fetchBanks();
      } else {
        setError(response.data.message || 'Failed to delete question bank.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting question bank.');
    }
  };

  const openQuickTest = (bank) => {
    setQuickTestBank(bank);
    setQuickTestCount(Math.min(10, bank.active_questions) || 1);
  };

  const startQuickTest = () => {
    navigate(`/admin-mock-exam-bank-preview/${quickTestBank.id}?count=${quickTestCount}`);
    setQuickTestBank(null);
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Question Banks</span></li>
              </ul>
            </div>
            <button
              type="button"
              className="btn btn-main rounded-pill py-9"
              onClick={() => (showForm ? resetForm() : setShowForm(true))}
            >
              <i className={`ph ${showForm ? 'ph-x' : 'ph-plus'} me-2`} /> {showForm ? 'Cancel' : 'Add Question Bank'}
            </button>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          {showForm && (
            <div className="card mb-24">
              <div className="card-header border-bottom border-gray-100">
                <h5 className="mb-0">{editingId ? 'Edit Question Bank' : 'Add New Question Bank'}</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-20">
                    <div className="col-sm-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Course <span className="text-red-500">*</span></label>
                      <select
                        className="form-control py-11"
                        value={form.course_id}
                        onChange={(e) => setForm({ ...form, course_id: e.target.value })}
                        disabled={saving || !!editingId}
                        required
                      >
                        <option value="">Select course...</option>
                        {courses.map((c) => (<option key={c.id} value={c.id}>{c.coursename}</option>))}
                      </select>
                    </div>
                    <div className="col-sm-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Bank Name <span className="text-red-500">*</span></label>
                      <input
                        type="text" className="form-control py-11" placeholder="e.g. SEO Basics"
                        value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={saving} required
                      />
                    </div>
                    <div className="col-sm-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Topic</label>
                      <input
                        type="text" className="form-control py-11" placeholder="e.g. Search Engine Optimization"
                        value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} disabled={saving}
                      />
                    </div>
                    <div className="col-sm-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Difficulty Level</label>
                      <select
                        className="form-control py-11"
                        value={form.difficulty_level}
                        onChange={(e) => setForm({ ...form, difficulty_level: e.target.value })}
                        disabled={saving}
                      >
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                        <option value="mixed">Mixed</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="h5 mb-8 fw-semibold font-heading">Description</label>
                      <textarea
                        className="form-control py-11" rows={3}
                        value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} disabled={saving}
                      />
                    </div>
                    <div className="col-12 flex-align justify-content-end gap-8">
                      <button type="button" className="btn btn-outline-main rounded-pill py-9" onClick={resetForm}>Cancel</button>
                      <button type="submit" className="btn btn-main rounded-pill py-9" disabled={saving}>
                        {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Bank'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="card mb-24">
            <div className="card-body">
              <div className="row g-16">
                <div className="col-md-3">
                  <label className="text-13 text-gray-500 mb-4 d-block">Search</label>
                  <input type="text" className="form-control py-9" placeholder="Search bank name..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="col-md-3">
                  <label className="text-13 text-gray-500 mb-4 d-block">Course</label>
                  <select className="form-control py-9" value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}>
                    <option value="">All Courses</option>
                    {courses.map((c) => (<option key={c.id} value={c.id}>{c.coursename}</option>))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="text-13 text-gray-500 mb-4 d-block">Topic</label>
                  <input type="text" className="form-control py-9" placeholder="Filter by topic..." value={topicFilter} onChange={(e) => setTopicFilter(e.target.value)} />
                </div>
                <div className="col-md-3">
                  <label className="text-13 text-gray-500 mb-4 d-block">Difficulty</label>
                  <select className="form-control py-9" value={difficultyFilter} onChange={(e) => setDifficultyFilter(e.target.value)}>
                    <option value="">All</option>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header border-bottom border-gray-100">
              <h5 className="mb-0">Question Banks ({filteredBanks.length})</h5>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : filteredBanks.length === 0 ? (
                <div className="text-center py-40 text-gray-400">No question banks found. Create one above.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Course</th>
                        <th>Topic</th>
                        <th>Difficulty</th>
                        <th>Questions (Active)</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBanks.map((b) => (
                        <tr key={b.id}>
                          <td className="fw-semibold" style={{ maxWidth: 160 }}>{b.name}</td>
                          <td style={{ maxWidth: 180, whiteSpace: 'normal' }} title={b.coursename}>{b.coursename}</td>
                          <td>{b.topic || '—'}</td>
                          <td className="text-capitalize">{b.difficulty_level}</td>
                          <td>{b.total_questions} ({b.active_questions})</td>
                          <td>
                            <span className={`badge ${b.status === 'active' ? 'bg-success-subtle text-success-emphasis' : 'bg-neutral-200 text-neutral-600'}`}>
                              {b.status}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex gap-6">
                              <button type="button" className="btn btn-sm btn-outline-main py-6 px-8" style={actionIconStyle} title="Questions" onClick={() => navigate(`/admin-mock-exam-questions/${b.id}`)}>
                                <i className="ph ph-list-checks" />
                              </button>
                              <button type="button" className="btn btn-sm py-6 px-8" style={{ ...actionIconStyle, ...solidGreen }} title="Quick Test" onClick={() => openQuickTest(b)} disabled={b.active_questions === 0}>
                                <i className="ph ph-play" />
                              </button>
                              <button type="button" className="btn btn-sm btn-outline-main py-6 px-8" style={actionIconStyle} title="Edit" onClick={() => startEdit(b)}>
                                <i className="ph ph-pencil-simple" />
                              </button>
                              <button type="button" className="btn btn-sm btn-outline-main py-6 px-8" style={actionIconStyle} title={b.status === 'active' ? 'Deactivate' : 'Activate'} onClick={() => toggleStatus(b)}>
                                <i className={`ph ${b.status === 'active' ? 'ph-eye-slash' : 'ph-eye'}`} />
                              </button>
                              <button type="button" className="btn btn-sm py-6 px-8" style={{ ...actionIconStyle, ...solidRed }} title="Delete" onClick={() => handleDelete(b)}>
                                <i className="ph ph-trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {quickTestBank && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(3,9,23,0.55)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div className="card border-0" style={{ maxWidth: 400, width: '100%' }}>
            <div className="card-body p-24">
              <h5 className="fw-bold mb-4">Quick Test</h5>
              <p className="text-13 text-gray-500 mb-16">{quickTestBank.name} — {quickTestBank.active_questions} active questions available</p>
              <label className="h5 mb-8 fw-semibold font-heading">How many questions?</label>
              <input
                type="number" min="1" max={quickTestBank.active_questions} className="form-control py-11"
                value={quickTestCount} onChange={(e) => setQuickTestCount(Math.min(Number(e.target.value) || 1, quickTestBank.active_questions))}
              />
              <p className="text-13 text-gray-400 mt-8 mb-0">
                This is a scratch test just for you — nothing is saved, and it won't affect any real exam or report.
              </p>
              <div className="d-flex justify-content-end gap-8 mt-20">
                <button type="button" className="btn btn-outline-main rounded-pill py-9 px-20" onClick={() => setQuickTestBank(null)}>Cancel</button>
                <button type="button" className="btn btn-main rounded-pill py-9 px-20" onClick={startQuickTest}>Start Test</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminMockExamBanks;
